import { useEffect, useState } from "react";
import { Database, Trash2, RefreshCw, Settings as SettingsIcon, AlertTriangle, BookOpen, Download, KeyRound, Eye, EyeOff } from "lucide-react";
import { API_BASE, api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import HelpTip from "@/components/HelpTip";

export default function Settings() {
    const { user } = useAuth();
    const [info, setInfo] = useState(null);
    const [loading, setLoading] = useState(false);
    const [confirm, setConfirm] = useState(false);
    const [manuals, setManuals] = useState([]);
    const [pwd, setPwd] = useState({ current: "", next: "", confirm: "" });
    const [showPwd, setShowPwd] = useState(false);
    const [savingPwd, setSavingPwd] = useState(false);

    const load = async () => {
        try { 
            const r = await api.get("/admin/system-info");
            if (r?.data) setInfo(r.data);
        } catch {}
        try {
            const r = await api.get("/manuals");
            if (Array.isArray(r?.data)) setManuals(r.data);
        } catch {}
    };
    useEffect(() => { load(); }, []);

    const seed = async () => {
        setLoading(true);
        try {
            const { data } = await api.post("/admin/seed-demo");
            toast.success(data.message);
            load();
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
        finally { setLoading(false); }
    };

    const clear = async () => {
        setConfirm(false);
        setLoading(true);
        try {
            const { data } = await api.post("/admin/clear-demo");
            toast.success(data.message);
            load();
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
        finally { setLoading(false); }
    };

    const wipeAll = async (keepClientsAgreements = true) => {
        const headline = keepClientsAgreements
            ? "⚠️ ESTO BORRA TODOS LOS DATOS OPERATIVOS de pruebas (servicios, cotizaciones, FRAP, facturas, ambulancias, personal, asistencia, etc.)\n\n✓ Se CONSERVAN Clientes y Convenios (los que ya tienes capturados).\n✓ También se conservan usuarios/logins, listas de precios, almacenes, integraciones y contactos de hospitales."
            : "🚨 BORRADO TOTAL: incluye también Clientes y Convenios (irreversible).\n\nSE CONSERVAN: usuarios/logins, listas de precios, integraciones, contactos hospitales.";
        const txt = window.prompt(`${headline}\n\nPara CONFIRMAR teclea exactamente:  BORRAR TODO`);
        if (txt !== "BORRAR TODO") {
            if (txt !== null) toast.warning("Texto no coincide. Operación cancelada.");
            return;
        }
        setLoading(true);
        try {
            const { data } = await api.post("/admin/wipe-operational", {
                confirm: "BORRAR TODO",
                keep_clients_agreements: keepClientsAgreements,
            });
            toast.success(data.message || `${data.total_deleted} registros eliminados.`);
            load();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Error al borrar datos");
        } finally {
            setLoading(false);
        }
    };

    const isAdmin = user?.role === "superadmin" || user?.role === "direccion";

    const SUBTITLES = {
        superadmin: "Administración total, finanzas y configuración",
        dispatch:   "Coordinación de servicios y seguimiento GPS",
        crew:       "Paramédico y operador en el celular",
        client:     "Portal: solicitar, dar seguimiento, facturas",
    };

    const changePassword = async (e) => {
        e.preventDefault();
        if (pwd.next.length < 8) return toast.error("Mínimo 8 caracteres");
        if (pwd.next !== pwd.confirm) return toast.error("Las contraseñas no coinciden");
        setSavingPwd(true);
        try {
            await api.post("/auth/change-password", {
                current_password: pwd.current,
                new_password: pwd.next,
                confirm_password: pwd.confirm,
            });
            toast.success("Contraseña actualizada");
            setPwd({ current: "", next: "", confirm: "" });
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "No se pudo cambiar");
        } finally {
            setSavingPwd(false);
        }
    };

    const downloadManual = async (m) => {
        try {
            const res = await fetch(`${API_BASE}/manuals/${m.role}`, { credentials: "include" });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const blob = await res.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = m.filename || `FindMed-Manual-${m.role}.pdf`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
            toast.success(`Descargado: ${m.title}`);
        } catch (e) {
            toast.error("No se pudo descargar el manual");
        }
    };

    return (
        <div className="max-w-4xl space-y-4" data-testid="settings-page">
            <div className="fm-card p-5">
                <div className="flex items-center gap-2 mb-4">
                    <SettingsIcon className="w-5 h-5 text-slate-700" />
                    <h2 className="font-display font-bold text-lg">Configuración del sistema</h2>
                    <HelpTip text="Vista general del sistema: cuántos usuarios, clientes, unidades, personal y servicios tienes registrados. Te ayuda a saber qué tan completa está tu información." />
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                    {info && [
                        ["Usuarios", info.users], ["Clientes", info.clients], ["Unidades", info.units],
                        ["Personal", info.personnel], ["Servicios", info.services], ["Facturas", info.invoices],
                        ["Registros demo", info.demo_records],
                    ].map(([l, v]) => (
                        <div key={l} className="border border-slate-200 rounded-sm p-3">
                            <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500">{l}</div>
                            <div className="font-mono font-bold text-xl tabular-nums">{v}</div>
                        </div>
                    ))}
                </div>
            </div>

            {manuals.length > 0 && (
            <div className="fm-card p-5 border-l-4 border-l-sky-500">
                <div className="flex items-start gap-3 mb-4">
                    <BookOpen className="w-6 h-6 text-sky-600 flex-shrink-0" strokeWidth={1.5} />
                    <div>
                        <h3 className="font-display font-bold text-base">
                            {isAdmin ? "Manuales operativos para capacitación" : "Tu manual de operación"}
                        </h3>
                        <p className="text-sm text-slate-600">
                            {isAdmin
                                ? "Descarga el manual en PDF correspondiente a cada rol. Están escritos en español, sin tecnicismos, listos para imprimir y entregar al personal."
                                : "Descarga tu manual en PDF para revisarlo o imprimirlo. Está escrito en español, sin tecnicismos."}
                        </p>
                    </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {manuals.map((m) => (
                        <button
                            key={m.role}
                            onClick={() => downloadManual(m)}
                            data-testid={`download-manual-${m.role}-btn`}
                            className="text-left border border-slate-200 hover:border-sky-400 hover:bg-sky-50/50 rounded-sm p-4 transition flex items-start gap-3 group"
                        >
                            <Download className="w-4 h-4 text-sky-600 mt-0.5 flex-shrink-0 group-hover:translate-y-0.5 transition" />
                            <div className="flex-1 min-w-0">
                                <div className="font-display font-bold text-sm text-slate-900">{m.title}</div>
                                <div className="text-xs text-slate-500 leading-snug">{SUBTITLES[m.role]}</div>
                            </div>
                        </button>
                    ))}
                </div>
            </div>
            )}

            <div className="fm-card p-5 border-l-4 border-l-amber-500">
                <div className="flex items-start gap-3 mb-4">
                    <Database className="w-6 h-6 text-amber-600 flex-shrink-0" strokeWidth={1.5} />
                    <div>
                        <h3 className="font-display font-bold text-base">Datos de demostración</h3>
                        <p className="text-sm text-slate-600">
                            Carga datos demo (clientes corporativos, ambulancias, personal, servicios, facturas e incidentes) para
                            ver el sistema operativo desde el primer minuto. <strong>Estos datos se identifican internamente
                            con la etiqueta <code className="font-mono bg-slate-100 px-1">_demo</code></strong> y se pueden eliminar
                            sin afectar tu información real.
                        </p>
                    </div>
                </div>

                {!isAdmin && (
                    <div className="text-xs text-slate-500 italic mb-3">Solo administradores pueden cargar o eliminar datos de demostración.</div>
                )}

                <div className="flex flex-wrap gap-3">
                    <button onClick={seed} disabled={!isAdmin || loading} className="fm-btn-primary" data-testid="seed-demo-btn" title="Carga clientes, ambulancias, personal, servicios, facturas e incidentes de prueba para entrenar al equipo o probar funciones sin afectar tus datos reales.">
                        <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Cargar datos demo
                    </button>
                    <button onClick={() => setConfirm(true)} disabled={!isAdmin || loading} className="fm-btn-outline border-red-300 text-red-700 hover:bg-red-50" data-testid="clear-demo-btn" title="Elimina ÚNICAMENTE los registros con etiqueta _demo. Tus clientes, ambulancias y servicios reales no se tocan. Úsalo antes del Deploy a producción.">
                        <Trash2 className="w-3.5 h-3.5" /> Limpiar datos demo
                    </button>
                </div>
            </div>

            {/* === Wipe operativo total === */}
            <div className="fm-card p-5 border-l-4 border-l-red-600">
                <div className="flex items-start gap-3 mb-4">
                    <AlertTriangle className="w-6 h-6 text-red-600 flex-shrink-0" strokeWidth={1.5} />
                    <div>
                        <h3 className="font-display font-bold text-base">🧹 Borrar TODOS mis datos de prueba</h3>
                        <p className="text-sm text-slate-600">
                            Si has estado capturando datos en pruebas (servicios, cotizaciones, ambulancias, personal, etc.) y quieres
                            dejar el sistema <strong>limpio para operación real</strong>, esta acción borra TODO lo operativo de un golpe.
                        </p>
                        <p className="text-xs text-slate-500 mt-2">
                            <strong className="text-emerald-700">SIEMPRE SE CONSERVA:</strong> Tus logins/usuarios, listas de precios configuradas, almacenes (sin stock), integraciones (Twilio/Resend) y directorio de hospitales.
                        </p>
                        <p className="text-xs text-slate-500 mt-1">
                            <strong className="text-red-700">SIEMPRE SE BORRA:</strong> Servicios, cotizaciones, facturas, pagos, FRAP, incidentes, mantenimientos, ambulancias, personal, asistencia, alertas, auditoría, movimientos de inventario, prospectos.
                        </p>
                    </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <button
                        onClick={() => wipeAll(true)}
                        disabled={!isAdmin || loading}
                        className="fm-btn-accent bg-emerald-600 hover:bg-emerald-700 border-emerald-600 text-white"
                        data-testid="wipe-keep-clients-btn"
                        title="Borra todo lo operativo PERO conserva los clientes y convenios que ya capturaste."
                    >
                        <Trash2 className="w-3.5 h-3.5" /> Borrar operación · CONSERVAR Clientes & Convenios
                    </button>
                    <button
                        onClick={() => wipeAll(false)}
                        disabled={!isAdmin || loading}
                        className="fm-btn-accent bg-red-600 hover:bg-red-700 border-red-600 text-white"
                        data-testid="wipe-all-btn"
                        title="Borra TODO incluyendo clientes y convenios. Irreversible."
                    >
                        <Trash2 className="w-3.5 h-3.5" /> Borrar TODO (incluye Clientes & Convenios)
                    </button>
                </div>
            </div>

            <div className="fm-card p-5 border-l-4 border-l-emerald-500">
                <div className="flex items-start gap-3 mb-4">
                    <KeyRound className="w-6 h-6 text-emerald-600 flex-shrink-0" strokeWidth={1.5} />
                    <div>
                        <h3 className="font-display font-bold text-base">Cambiar mi contraseña</h3>
                        <p className="text-sm text-slate-600">
                            Por seguridad, cambia tu contraseña con regularidad y nunca la compartas.
                            Mínimo 8 caracteres.
                        </p>
                    </div>
                </div>
                <form onSubmit={changePassword} className="grid grid-cols-1 md:grid-cols-3 gap-3 max-w-3xl">
                    <div>
                        <label className="fm-label">Contraseña actual</label>
                        <input
                            type={showPwd ? "text" : "password"}
                            required
                            value={pwd.current}
                            onChange={(e) => setPwd({ ...pwd, current: e.target.value })}
                            className="fm-input"
                            placeholder="••••••••"
                            data-testid="current-password-input"
                            autoComplete="current-password"
                        />
                    </div>
                    <div>
                        <label className="fm-label">Nueva contraseña</label>
                        <input
                            type={showPwd ? "text" : "password"}
                            required
                            minLength={8}
                            value={pwd.next}
                            onChange={(e) => setPwd({ ...pwd, next: e.target.value })}
                            className="fm-input"
                            placeholder="Mínimo 8 caracteres"
                            data-testid="new-password-input"
                            autoComplete="new-password"
                        />
                    </div>
                    <div>
                        <label className="fm-label">Confirmar nueva</label>
                        <input
                            type={showPwd ? "text" : "password"}
                            required
                            minLength={8}
                            value={pwd.confirm}
                            onChange={(e) => setPwd({ ...pwd, confirm: e.target.value })}
                            className="fm-input"
                            placeholder="Repite la contraseña"
                            data-testid="confirm-password-input"
                            autoComplete="new-password"
                        />
                    </div>
                    <div className="md:col-span-3 flex flex-wrap items-center gap-3">
                        <button
                            type="button"
                            onClick={() => setShowPwd((v) => !v)}
                            className="text-xs text-slate-500 hover:text-slate-900 flex items-center gap-1"
                            data-testid="toggle-show-pwd"
                        >
                            {showPwd ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                            {showPwd ? "Ocultar contraseñas" : "Mostrar contraseñas"}
                        </button>
                        <div className="flex-1"></div>
                        <button
                            type="submit"
                            disabled={savingPwd}
                            className="fm-btn-primary"
                            data-testid="change-password-btn"
                        >
                            <KeyRound className="w-3.5 h-3.5" />
                            {savingPwd ? "Guardando..." : "Cambiar contraseña"}
                        </button>
                    </div>
                </form>
            </div>

            <div className="fm-card p-5">
                <h3 className="font-display font-bold text-base mb-3">Cuenta actual</h3>
                <dl className="space-y-1 text-sm">
                    <div className="flex justify-between border-b border-slate-100 py-1"><dt className="text-slate-500">Nombre</dt><dd className="font-medium">{user?.name}</dd></div>
                    <div className="flex justify-between border-b border-slate-100 py-1"><dt className="text-slate-500">Email</dt><dd className="font-mono text-xs">{user?.email}</dd></div>
                    <div className="flex justify-between border-b border-slate-100 py-1"><dt className="text-slate-500">Rol</dt><dd className="font-mono text-xs">{user?.role}</dd></div>
                </dl>
            </div>

            {confirm && (
                <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-sm max-w-md w-full p-5">
                        <div className="flex items-center gap-3 mb-3">
                            <AlertTriangle className="w-6 h-6 text-red-600" />
                            <h3 className="font-display font-bold text-lg">Confirmar eliminación</h3>
                        </div>
                        <p className="text-sm text-slate-600 mb-4">
                            Se eliminarán todos los registros marcados como <code className="font-mono bg-slate-100 px-1">_demo</code>.
                            Tu información real (creada manualmente) <strong>no se verá afectada</strong>.
                        </p>
                        <div className="flex justify-end gap-2">
                            <button onClick={() => setConfirm(false)} className="fm-btn-outline">Cancelar</button>
                            <button onClick={clear} className="fm-btn-accent" data-testid="confirm-clear-btn">Sí, eliminar demo</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
